import type { CoReviewComment } from "../../../shared/interfaces/CoReviewComment";

export interface CycleInfo {
  maxCommentCycle: number;
  isNewUncommentedCycle: boolean;
  currentCycle: number;
  currentCycleComments: CoReviewComment[];
  priorCycleComments: CoReviewComment[];
}

/**
 * Cycle numbers come from buildCycleByVersionNumber (1-indexed) attached
 * during decode. The "current" cycle is anchored to the artifact's
 * versionNumber, NOT to the highest cycle present in comments — otherwise,
 * immediately after the officer resubmits (artifact bumped, no new CO
 * comments yet) prior-cycle comments would still register as "current" and
 * clutter the inline view.
 *
 * Extracted verbatim from CoReviewPage; callers must only invoke this once
 * comments and artifactVersion come from the same loaded snapshot (never
 * comments-with-null-artifactVersion mid-load).
 */
export function deriveCycleInfo(
  comments: CoReviewComment[],
  artifactVersion: number | null,
): CycleInfo {
  const maxCommentCycle = comments.length
    ? Math.max(...comments.map((c) => c.reviewCycle))
    : 0;
  const knownVersions = new Set(comments.map((c) => c.reportVersionNumber));
  const isNewUncommentedCycle =
    artifactVersion != null && !knownVersions.has(artifactVersion);
  const currentCycle = isNewUncommentedCycle
    ? maxCommentCycle + 1
    : maxCommentCycle || 1;
  const currentCycleComments = comments.filter(
    (c) => c.reviewCycle === currentCycle,
  );
  const priorCycleComments = comments.filter(
    (c) => c.reviewCycle < currentCycle,
  );
  return {
    maxCommentCycle,
    isNewUncommentedCycle,
    currentCycle,
    currentCycleComments,
    priorCycleComments,
  };
}
